import Swiper from 'swiper';
import { FreeMode, Mousewheel, Navigation, Pagination } from 'swiper/modules';
import arrowRight from '@fortawesome/fontawesome-free/svgs/solid/arrow-right.svg';

document.addEventListener('DOMContentLoaded', function () {
    const contextElements = document.querySelectorAll('.wp-block-jims-wp-business-carousel');
    if (contextElements.length === 0) {
        return;
    }

    // Each carousel keeps isolated pagination, Swiper, and observer state.
    contextElements.forEach(initCarousel);

    function constructBusinessFindUrl(ctx) {
        // Reuse the server-provided REST endpoint and filters so appended slides match the initial PHP render.
        const url = new URL(ctx.baseUrl);
        url.search = new URLSearchParams({
            state: ctx.state,
            region: ctx.region,
            suburb: ctx.suburb,
            postcode: ctx.postcode,
            per_page: ctx.perPage,
            page: ctx.page,
        }).toString();

        return url.toString();
    }

    function ucWords(str) {
        // Mirror the PHP title casing enough for appended slides to look like server-rendered slides.
        const result = (str ?? '').toLowerCase().replace(/(^([a-zA-Z\p{M}]))|([ -][a-zA-Z\p{M}])/g,
            function (s) {
                return s.toUpperCase();
            });
        return result;
    }

    function initCarousel(contextElement) {
        // The context object is intentionally per DOM node; pages can contain multiple carousel blocks.
        const ctx = {
            baseUrl: contextElement.getAttribute('data-base-url') ?? '',
            siteUrl: contextElement.getAttribute('data-site-url') ?? '',
            state: contextElement.getAttribute('data-state') ?? '',
            region: contextElement.getAttribute('data-region') ?? '',
            suburb: contextElement.getAttribute('data-suburb') ?? '',
            postcode: contextElement.getAttribute('data-postcode') ?? '',
            areaNameField: contextElement.getAttribute('data-area-name-field') ?? '',
            perPage: contextElement.getAttribute('data-per-page') ?? '20',
            page: 1,
            resultsElement: contextElement.querySelector(':scope > .swiper-wrapper'),
            intersectionObserver: null,
            isFetching: false,
            swiper: new Swiper(contextElement, {
                // Swiper mutates the block wrapper, so selectors must stay scoped to this instance.
                createElements: true,
                slidesPerView: 'auto',
                modules: [FreeMode, Mousewheel, Navigation, Pagination],
                freeMode: {
                    enabled: true,
                    sticky: true,
                },
                observer: true,
                observeParents: true,
                observeSlideChildren: true,
                mousewheel: {
                    enabled: true,
                },
                pagination: {
                    el: contextElement.querySelector(':scope > .swiper-pagination'),
                    clickable: true,
                },
                spaceBetween: 32,
            }),
        };

        if (ctx.resultsElement == null) {
            return;
        }

        // The last rendered slide acts as the infinite-load sentinel.
        ctx.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    ctx.page++;
                    fetchDataAndRender(ctx, true);
                }
            });
        });

        const lastElement = ctx.resultsElement.querySelector(':scope > div:last-child');
        if (lastElement != null) {
            ctx.intersectionObserver.observe(lastElement);
        }
    }

    function renderBusinesses(response, ctx, append) {
        // Unexpected response shapes should stop the in-flight state without clearing existing slides.
        if (!response?.result?.data) {
            ctx.isFetching = false;
            return;
        }

        const franchises = response.result.data ?? [];
        let lastElement;

        if (!append) {
            ctx.resultsElement.replaceChildren();
        }

        for (let franchise of franchises) {
            let franchiseAreaName = '';
            // Match the editor's area-name setting and the PHP render branch.
            switch (ctx.areaNameField) {
                case 'custom_region_name':
                    franchiseAreaName = franchise.business_area?.custom_region_name ?? '';
                    break;
                case 'region.name':
                    franchiseAreaName = franchise.region?.name ?? '';
                    break;
                default:
                    franchiseAreaName = ucWords(franchise.business_area?.name ?? '');
            }

            const franchiseName = ucWords(franchise.handler?.name ?? '');
            const stateCode = (franchise.state?.code ?? '').toLowerCase();
            const franchiseSlug = franchise.business_area?.slug ?? '';
            const handlerPhoto = franchise.handler?.photo?.url ?? '';

            // Skip partial API records rather than appending incomplete cards.
            if (!franchiseAreaName || !franchiseName || !stateCode || !franchiseSlug || !handlerPhoto) {
                continue;
            }

            // Public franchise URLs intentionally use /franchise/{state}/{slug}.
            // Keep this in sync with frontend.template.php, SEOManager, and sitemap providers.
            const franchiseUrl = `${ctx.siteUrl}/franchise/${stateCode}/${franchiseSlug}`;

            const tileEl = document.createElement('div');
            tileEl.classList.add('swiper-slide');

            const aEl = document.createElement('a');
            aEl.href = franchiseUrl;
            aEl.title = `Navigate to business ${franchiseName} in ${franchiseAreaName}`;

            const imgEl = document.createElement('img');
            imgEl.src = handlerPhoto;
            imgEl.alt = `Photo profile of ${franchiseName} in ${franchiseAreaName}`;

            const divEl = document.createElement('div');

            const titleEl = document.createElement('h3');
            titleEl.innerText = franchiseAreaName;

            const arrowEl = document.createElement('i');
            arrowEl.className = 'fa-svg is-arrow-right';
            arrowEl.innerHTML = `<span style="mask-image: url('${arrowRight}');"></span>`;

            divEl.appendChild(titleEl);
            divEl.appendChild(arrowEl);

            aEl.appendChild(imgEl);
            aEl.appendChild(divEl);

            tileEl.appendChild(aEl);

            ctx.resultsElement.appendChild(tileEl);
            lastElement = tileEl;
        }

        ctx.swiper.update();

        // Observe the newest slide only when the API says more pages are available.
        ctx.intersectionObserver.disconnect();
        if (lastElement != null && response.result.meta?.has_more_pages === true) {
            ctx.intersectionObserver.observe(lastElement);
        }

        ctx.isFetching = false;
    }

    async function fetchDataAndRender(ctx, append) {
        // IntersectionObserver may fire repeatedly while the sentinel is visible; keep one request active.
        if (ctx.isFetching === true) {
            return;
        }

        ctx.isFetching = true;

        const url = constructBusinessFindUrl(ctx);

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Business carousel request failed: ${response.status}`);
            }

            const responseData = await response.json();
            renderBusinesses(responseData, ctx, append);
        }
        catch (error) {
            console.error(error);
            ctx.isFetching = false;
        }
    }
});
